/**
 * Signup Form Component
 *
 * Form for user registration with email/password.
 */

import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { useAuth } from '../hooks/useAuth'
import type { SignupCredentials } from '../types/auth.types'

interface SignupFormProps {
  onSuccess?: () => void
  onSwitchToLogin?: () => void
}

export const SignupForm = ({ onSuccess, onSwitchToLogin }: SignupFormProps) => {
  const { signup, loading, error, setError } = useAuth()

  const [credentials, setCredentials] = useState<SignupCredentials>({
    email: '',
    password: '',
    displayName: '',
  })

  const [confirmPassword, setConfirmPassword] = useState('')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    // Validate password match
    if (credentials.password !== confirmPassword) {
      setError('비밀번호가 일치하지 않습니다')
      return
    }

    // Validate password strength (minimum 6 characters)
    if (credentials.password.length < 6) {
      setError('비밀번호는 최소 6자 이상이어야 합니다')
      return
    }

    try {
      await signup(credentials)
      onSuccess?.()
    } catch (error) {
      // Error is already set in the store
      console.error('Signup failed:', error)
    }
  }

  return (
    <div className="w-full max-w-md space-y-6">
      <div className="space-y-2 text-center">
        <h1 className="text-3xl font-bold">회원가입</h1>
        <p className="text-muted-foreground">새 계정을 만드세요</p>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="displayName">이름</Label>
          <Input
            id="displayName"
            type="text"
            placeholder="홍길동"
            value={credentials.displayName}
            onChange={(e) => setCredentials({ ...credentials, displayName: e.target.value })}
            disabled={loading}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="email">이메일</Label>
          <Input
            id="email"
            type="email"
            placeholder="name@example.com"
            value={credentials.email}
            onChange={(e) => setCredentials({ ...credentials, email: e.target.value })}
            required
            disabled={loading}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="password">비밀번호</Label>
          <Input
            id="password"
            type="password"
            placeholder="••••••••"
            value={credentials.password}
            onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
            required
            disabled={loading}
            minLength={6}
          />
          <p className="text-xs text-muted-foreground">최소 6자 이상</p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="confirmPassword">비밀번호 확인</Label>
          <Input
            id="confirmPassword"
            type="password"
            placeholder="••••••••"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
            disabled={loading}
            minLength={6}
          />
        </div>

        <Button type="submit" className="w-full" disabled={loading}>
          {loading ? '회원가입 중...' : '회원가입'}
        </Button>
      </form>

      {onSwitchToLogin && (
        <div className="text-center text-sm">
          <span className="text-muted-foreground">이미 계정이 있으신가요? </span>
          <Button
            type="button"
            variant="link"
            className="p-0"
            onClick={onSwitchToLogin}
            disabled={loading}
          >
            로그인
          </Button>
        </div>
      )}
    </div>
  )
}
